// Staff waiting screen (roadmap #41), route /staff/dashboard. The staff keeps this open
// to receive calls in the browser: it registers the web device (staff-join on the shared
// socket + FCM token) and routes incoming-call notifications to the ringing screen.
// Mirrors the always-logged-in /#/dashboard behaviour of the previous client.
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import BrandLockup from '../components/BrandLockup'
import LanguageSwitch from '../components/LanguageSwitch'
import { useNavigate } from 'react-router-dom'
import { socket, readyTheSocketConnection, onSocketConnected } from '../core/socket'
import { fetchIceConfig } from '../core/iceConfig'
import { globals } from '../core/globals'
import { getAuthUser } from '../core/auth'
import { staffLogout } from './staffAuth'
import { primeRingtone } from './ringtone'
import { t } from '../i18n'
import DesktopSetup from './DesktopSetup'
import { consumeIncomingCallLink } from './incomingCallLink'
import {
  primeIncomingCallFromCallId,
  processSilentNotification,
  registerFcmToken,
  registerMessagingServiceWorker,
  subscribeForegroundMessages,
  subscribeServiceWorkerMessages,
} from './messaging'

const StaffDashboard = () => {
  const navigate = useNavigate()
  const user = getAuthUser()
  // A notification acted on with no window open lands HERE, carrying the call in the link
  // (see the service worker). Guarded so the call is opened once and never replayed by a
  // reload or a re-render.
  const notificationLinkHandled = useRef(false)
  const [connected, setConnected] = useState(socket.connected)
  const [notificationsReady, setNotificationsReady] = useState(
    typeof Notification !== 'undefined' && Notification.permission === 'granted'
  )

  useEffect(() => {
    let cleanupSwMessages = () => {}

    const init = async () => {
      // ICE CONFIG IS NEEDED BEFORE THE FIRST CALL (THE PEER CONNECTION READS IT).
      fetchIceConfig()

      // READY THE SHARED SOCKET AND REGISTER THIS STAFF EMAIL (staff-join). RE-REGISTER ON
      // EVERY RECONNECT — THE SERVER ROUTES CALLS BY THE REGISTERED EMAIL.
      try {
        await readyTheSocketConnection()
      } catch (e) {
        console.log('socket not ready yet, will register on connect', e)
      }
      onSocketConnected()
      socket.on('connect', onSocketConnected)

      // REGISTER THE WEB PUSH TOKEN AND THE BACKGROUND SERVICE WORKER.
      if (user?.user_id) registerFcmToken(user.user_id)
      registerMessagingServiceWorker()

      // FOREGROUND PUSHES AND SERVICE-WORKER BRIDGE BOTH OPEN THE INCOMING-CALL SCREEN.
      subscribeForegroundMessages((path, opts) => navigate(path, opts))
      cleanupSwMessages = subscribeServiceWorkerMessages(
        (path, opts) => navigate(path, opts),
        () => {}
      )

      // DEV-ONLY HOOK SO LOCAL E2E CAN INJECT AN INCOMING-CALL PUSH (FCM WEB PUSH IS NOT
      // DELIVERED TO HEADLESS BROWSERS — THE SAME STUB THE RESILIENCE HARNESS USED FOR THE
      // STAFF SIMULATOR). IT EXERCISES THE REAL processSilentNotification PATH.
      if (import.meta.env.DEV) {
        ;(window as any).__staffSimulateIncoming = (data: any) =>
          processSilentNotification(data, (path, opts) => navigate(path, opts))
      }
    }
    init()

    const onConnect = () => setConnected(true)
    const onDisconnect = () => setConnected(false)
    socket.on('connect', onConnect)
    socket.on('disconnect', onDisconnect)

    // Ring-when-unattended: browsers only let audio start on a page the user has
    // interacted with, and the incoming-call screen opens by itself from a push with
    // the window minimised. Prime the ringtone on the first interaction of this
    // waiting screen so the later, unattended play is allowed (see staff/ringtone.ts).
    const onFirstInteraction = () => {
      void primeRingtone()
    }
    window.addEventListener('pointerdown', onFirstInteraction, { once: true })
    window.addEventListener('keydown', onFirstInteraction, { once: true })

    return () => {
      window.removeEventListener('pointerdown', onFirstInteraction)
      window.removeEventListener('keydown', onFirstInteraction)
      socket.off('connect', onSocketConnected)
      socket.off('connect', onConnect)
      socket.off('disconnect', onDisconnect)
      cleanupSwMessages()
    }
  }, [])

  // OPEN THE CALL A NOTIFICATION WAS ACTED ON WHEN NO WINDOW WAS RUNNING.
  //
  // The service worker can only hand the call to an application window that already exists.
  // With none open it opens one, and the call travels in the link - which main.tsx captures at
  // boot, because the route guard and the sign-on round-trip drop it long before this screen
  // mounts. Here the call is rebuilt from its identifier and the ringing screen takes over,
  // auto-accepting when that is the button that was pressed.
  useEffect(() => {
    if (notificationLinkHandled.current) return
    const link = consumeIncomingCallLink()
    if (!link) return
    notificationLinkHandled.current = true

    ;(async () => {
      const opened = await primeIncomingCallFromCallId(link.callId)
      if (!opened) {
        // The call is over, or too old to answer. Say so and stay on the dashboard.
        toast(t.staffCallNoLongerActive)
        return
      }
      navigate('/staff/incomingcall', {
        replace: true,
        state: {
          callArrivedAt: Date.now(),
          autoAccept: link.action === 'CALL_ACCEPT',
          autoReject: link.action === 'CALL_REJECT',
        },
      })
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const enableNotifications = () => {
    if (typeof Notification === 'undefined') return
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        setNotificationsReady(true)
        if (user?.user_id) registerFcmToken(user.user_id)
      }
    })
  }

  const onLogout = async () => {
    await staffLogout()
    navigate('/staff', { replace: true })
  }

  const staffName = user?.full_name || globals.email || ''

  return (
    <div className="cf-page">
      <div className="cf-card cf-staff-card">
        <BrandLockup />
        <div className={`cf-status-dot ${connected ? 'online' : 'offline'}`} />
        <h1 className="cf-title">{connected ? t.staffAvailable : t.staffReconnecting}</h1>
        <p className="cf-subtitle">
          {connected ? t.staffAvailableHint : t.staffReconnectingHint}
        </p>
        {staffName && <p className="cf-staff-name">{staffName}</p>}

        {!notificationsReady && (
          <div className="cf-help">
            {t.staffNotificationsHelp}
            <button className="cf-btn cf-btn-secondary" onClick={enableNotifications}>
              {t.staffEnableNotifications}
            </button>
          </div>
        )}

        <DesktopSetup />

        <button className="cf-btn cf-btn-secondary cf-staff-logout" onClick={onLogout}>
          {t.staffLogout}
        </button>

        <LanguageSwitch />
      </div>
    </div>
  )
}

export default StaffDashboard
